import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';
import { contentPrinciples, siteInfo, siteStats, testimonials } from '@/mocks/site';
import { momentCategories } from '@/mocks/moments';

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  useSeo({
    title: `关于我们｜${siteInfo.name}的内容定位与整理原则`,
    description:
      '了解瓜田快报的网站定位、内容原则与联系方式。我们专注私拍吃瓜名场面与抓拍整理,只做观看顺序的说明,不收录涉及隐私的内容。',
    keywords: '关于瓜田快报,名场面整理原则,私拍吃瓜内容定位,联系我们',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: `关于我们｜${siteInfo.name}`,
        url: `${siteInfo.domain}/about`,
        description: siteInfo.description,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.domain,
        email: siteInfo.email,
        slogan: siteInfo.slogan,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.domain },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.domain}/about` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="px-4 md:px-8 pt-8 md:pt-10 pb-12 md:pb-16 bg-background-100">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-sm text-primary-700">
                <i className="ri-plant-line"></i>
                关于 {siteInfo.name}
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
                把散落的名场面,整理成看得下去的顺序
              </h1>
              <p className="mt-5 text-sm md:text-base leading-loose text-foreground-600 max-w-2xl">
                {siteInfo.description}
              </p>
              <p className="mt-4 text-sm md:text-base leading-loose text-foreground-600 max-w-2xl">
                我们不做单纯的搬运,也不靠标题党吸引点击。每一条内容都会经过二次整理:确认场景、标注时间点、写清
                为什么值得看。目标是让你在最短的时间里,抓住最值得看的那几秒。
              </p>
            </div>

            <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20amber%20studio%20space%20with%20soft%20spotlights%2C%20abstract%20editing%20desk%20and%20monitors%2C%20cozy%20blurred%20bokeh%2C%20cinematic%20editorial%20style%2C%20no%20people%2C%20warm%20neutral%20palette&width=900&height=700&seq=gt-about-studio&orientation=landscape"
                alt="暖光工作室里整理内容的场景图"
                title="瓜田快报 · 内容整理工作场景"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {siteStats.map((stat) => (
              <div key={stat.label} className="p-5 rounded-lg bg-background-50 border border-background-200">
                <p className="font-heading text-2xl md:text-3xl text-primary-700">{stat.value}</p>
                <p className="mt-1 text-xs md:text-sm text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-950">我们的内容原则</h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-2xl">
            内容整理这件事,做法比内容本身更重要。以下四条,是我们判断一条素材该不该收录、该怎么写的标准。
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {contentPrinciples.map((item) => (
              <article key={item.title} className="p-6 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-xl text-foreground-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-10">
            <div>
              <span className="inline-flex items-center gap-2 text-sm text-accent-700">
                <i className="ri-chat-smile-2-line"></i>
                用户口碑
              </span>
              <h2 className="mt-2 font-heading text-3xl md:text-4xl text-foreground-950">他们在这样用瓜田快报</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                有人把它当碎片时间的解压入口,有人把它当写作时的参考资料。不同用法,需求其实只有一个:找得到、看得完。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((item) => (
                <figure key={item.name} className="p-5 rounded-lg bg-background-50 border border-background-200">
                  <div className="flex items-center gap-1 text-secondary-500">
                    {[0, 1, 2, 3, 4].map((star) => (
                      <i key={star} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-800">
                      <i className="ri-user-3-line"></i>
                    </span>
                    <span>
                      <span className="block text-sm text-foreground-900">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="px-4 md:px-8 py-14 md:py-20 bg-background-50 scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950">联系我们</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                发现值得记录的抓拍、想推荐的分类方向,或者对内容整理有不同意见,都欢迎直接发邮件。我们通常在两个工作日内回复。
              </p>

              <ul className="mt-7 flex flex-col gap-4">
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line"></i>
                  </span>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="text-sm text-foreground-800 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {siteInfo.email}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className="ri-question-answer-line"></i>
                  </span>
                  <span className="text-sm text-foreground-800">常见问题可先看首页 FAQ 区块</span>
                </li>
              </ul>

              <a
                href={`mailto:${siteInfo.email}?subject=${encodeURIComponent('线索投稿')}`}
                className="mt-7 inline-flex px-5 py-2.5 rounded-md text-sm whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                发送线索邮件
              </a>
            </div>

            <div className="p-6 md:p-7 rounded-lg bg-background-100 border border-background-200">
              <h3 className="font-heading text-xl text-foreground-950">按分类继续浏览</h3>
              <p className="mt-3 text-sm text-foreground-600">选一个方向,直接进入对应的名场面整理。</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {momentCategories.slice(1).map((item) => (
                  <Link
                    key={item}
                    to={`/moments?category=${encodeURIComponent(item)}`}
                    className="px-3.5 py-2 rounded-full text-sm bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}